import {createRouter, createWebHistory} from 'vue-router'
import store from '@/store'
import { refreshToken } from '@/api'

const routes = [
    {
        path: '/login',
        name: 'login',
        component: () => import(`@/views/login/LoginView`),
        meta: { isPublic: true, title: '登录' }
    },
    {
        path: '/',
        name: '',
        redirect: '/dashboard'
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/content/DashboardView'),
        meta: { title: '工作台' }
    },
    {
        path: '/payRecord',
        name: 'payRecord',
        component: () => import(`@/views/content/PayRecordView`),
        meta: { title: '支出记录' }
    },
    {
        path: '/payRecord/edit',
        name: 'payRecordEdit',
        component: () => import(`@/views/content/PayRecordEditView`),
        meta: { title: '新增支出' }
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import(`@/views/content/SettingsView`),
        meta: { title: '设置' }
    },
    {
        path: '/statement',
        name: 'statement',
        component: () => import(`@/views/content/StatementView`),
        meta: { title: '支出总览' }
    },
    {
        path: '/salary',
        name: 'salary',
        component: () => import(`@/views/content/SalaryView`),
        meta: { title: '工资记录' }
    },
    {
        path: '/loanRecord',
        name: 'loanRecord',
        component: () => import(`@/views/content/LoanRecordView`),
        meta: { title: '借贷记录' }
    },
    {
        path: '/todoRecord',
        name: 'todoRecord',
        component: () => import('@/views/content/TodoRecordView'),
        meta: { title: '待办记录' }
    },
    {
        path: '/todoRecord/edit',
        name: 'todoRecordEdit',
        component: () => import('@/views/content/TodoRecordEditView'),
        meta: { title: '新增待办' }
    },
    {
        path: '/attendance',
        name: 'attendance',
        component: () => import('@/views/content/AttendanceView'),
        meta: { title: '考勤打卡' }
    },
    {
        path: '/document',
        name: 'document',
        component: () => import('@/views/content/DocumentView'),
        meta: { title: '文档' }
    },
    {
        path: '/accountPassword',
        name: 'accountPassword',
        component: () => import('@/views/content/AccountPasswordView'),
        meta: { requiresRoot: true, title: '账号密码管理' }
    },
    {
        path: '/accountPassword/edit',
        name: 'accountPasswordEdit',
        component: () => import('@/views/content/AccountPasswordEditView'),
        meta: { requiresRoot: true, title: '新增账号密码' }
    },
    {
        path: '/userCenter',
        name: 'userCenter',
        component: () => import(`@/views/content/UserCenterView`),
        meta: { requiresRoot: true, title: '用户管理' }
    },
    {
        path: '/fileManagement',
        name: 'fileManagement',
        component: () => import('@/views/content/FileManagementView'),
        meta: { requiresRoot: true, title: '文件与图片管理' }
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

router.beforeEach((to, from, next) => {
    const isLoggedIn = store.getters.isLoggedIn
    const userInfo = store.getters.userInfo
    
    // 如果用户已登录且正在访问受保护页面，需要检查token是否有效
    if (isLoggedIn && !to.meta.isPublic) {
        const expireTime = localStorage.getItem('expireTime')
        const currentTime = new Date().getTime()
        
        // Token已过期
        if (expireTime && currentTime >= expireTime) {
            store.commit('clearAuth')
            next({
                path: '/login',
                query: { redirect: to.fullPath }
            })
            return
        }
        
        // 检查是否需要刷新token（离过期1小时时）
        if (expireTime && currentTime + 60 * 60 * 1000 > expireTime) {
            // 刷新失败不立即登出：token 是否真正失效由后续请求的 401 决定，
            // 避免开发期后端短暂不可用/接口抖动时被误登出
            refreshToken().catch(() => {})
        }
    }
    
    if (!to.meta.isPublic && !isLoggedIn) {
        // 如果访问的不是公共页面且未登录，跳转到登录页
        next({
            path: '/login',
            query: { redirect: to.fullPath }
        })
    } else if (to.path === '/login' && isLoggedIn) {
        // 如果已经登录且访问登录页，重定向到首页
        next('/')
    } else if (to.meta.requiresRoot && userInfo.role !== 'ROOT') {
        // 如果页面需要ROOT权限但用户不是ROOT，禁止访问
        next('/')
    } else {
        next()
    }
})

// 启动后，设置定期检查和刷新token的定时器
router.afterEach((to) => {
    // 设置页面标题
    if (to.meta && to.meta.title) {
        document.title = to.meta.title
    } else {
        document.title = 'My Statement' // 默认标题
    }
    
    // 仅在有token时设置定时器
    if (!window.__tokenRefreshTimer && localStorage.getItem('token')) {
        window.__tokenRefreshTimer = setInterval(() => {
            const token = localStorage.getItem('token')
            const expireTime = localStorage.getItem('expireTime')
            const currentTime = new Date().getTime()
            
            // token不存在，清除定时器
            if (!token) {
                if (window.__tokenRefreshTimer) {
                    clearInterval(window.__tokenRefreshTimer)
                    window.__tokenRefreshTimer = null
                }
                return
            }
            
            // token已过期
            if (expireTime && currentTime >= expireTime) {
                store.commit('clearAuth')
                if (window.__tokenRefreshTimer) {
                    clearInterval(window.__tokenRefreshTimer)
                    window.__tokenRefreshTimer = null
                }
                router.push('/login').catch(() => {})
                return
            }
            
            // 检查是否需要刷新token（离过期1小时时）
            if (expireTime && currentTime + 60 * 60 * 1000 > expireTime) {
                refreshToken().then(res => {
                    if (res && res.code === 200) {
                        localStorage.setItem('expireTime', res.data.expireTime)
                    }
                }).catch(() => {
                    // 刷新失败不立即登出：交由后续请求的 401 处理，避免开发期误登出
                })
            }
        }, 10 * 60 * 1000) // 每10分钟检查一次
    }
})

export default router
